<template>
  <div>
    <a-card style="margin: 20px">
      <template #title>
        <div class="top-title">
          <span>订单详情</span>
          <a-button @click="$router.go(-1)">返回</a-button>
        </div>
      </template>

      <div v-if="orderData" class="detail-content">
        <!-- 基本信息 -->
        <a-card :bordered="false" class="detail-section" size="small" title="订单信息">
          <a-descriptions :column="3" bordered size="small">
            <a-descriptions-item label="订单号">
              <span class="detail-value">{{ orderData.orderNo }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="商户编号">
              <span class="detail-value">{{ orderData.merchantNo }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="商户名称">
              <span class="detail-value">{{ orderData.merchantName }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="个体户账户">
              <WalletInfo :account-name="orderData.accountName" :account-no="orderData.accountNo" :show-account-name="true"/>
            </a-descriptions-item>
            <a-descriptions-item label="订单金额">
              <Balance :amount="orderData.totalAmount" prefix="￥ "></Balance>
            </a-descriptions-item>
            <a-descriptions-item v-if="orderData.serviceFee > 0 && $authority('taxOrderFee')" label="服务费">
              <Balance :amount="orderData.serviceFee" prefix="￥ "></Balance>
            </a-descriptions-item>
            <a-descriptions-item label="订单状态">
              <a-tag :color="taxDict.orderStatus[orderData.status]?.color || 'default'">
                {{ taxDict.orderStatus[orderData.status]?.label || orderData.status }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="创建时间">
              <span class="detail-value">{{ formatDateTime(orderData.createTime) }}</span>
            </a-descriptions-item>
            <a-descriptions-item v-if="orderData.failReason" :span="2" label="失败原因">
              <span style="color: #ff4d4f;">{{ orderData.failReason }}</span>
            </a-descriptions-item>
            <a-descriptions-item v-if="orderData.remark" :span="2" label="摘要">
              <span>{{ orderData.remark }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 服务费记录 -->
        <a-card v-if="orderData.serviceFeeRecord  && $authority('taxOrderFee')" :bordered="false" class="detail-section" size="small" title="服务费归集记录">
          <template #extra>
            <a-button
                v-if="orderData.serviceFeeRecord?.paymentStatus === 3"
                icon="redo"
                size="small"
                type="primary"
                @click="retryServiceFee"
            >
              重试
            </a-button>
          </template>
          <a-table
              :columns="serviceFeeColumns"
              :data-source="orderData.serviceFeeRecord ? [orderData.serviceFeeRecord] : []"
              :pagination="false"
              row-key="id"
              size="small"
          >
            <template slot="feeAmount" slot-scope="text">
              <Balance :amount="text" prefix="￥"></Balance>
            </template>
            <template slot="recordStatus" slot-scope="text">
              <a-tag :color="taxDict.serviceFeeStatus[text]?.color || 'default'">
                {{ taxDict.serviceFeeStatus[text]?.label || text }}
              </a-tag>
            </template>
            <template slot="settleTime" slot-scope="text">
              {{ formatDateTime(text) }}
            </template>
            <template slot="failReason" slot-scope="text">
              <span v-if="text" style="color: #ff4d4f;">{{ text }}</span>
              <span v-else>-</span>
            </template>
            <template slot="remark" slot-scope="text">
              <span>{{ text || '-' }}</span>
            </template>
            <template slot="sfAccountNo" slot-scope="text, record">
              <WalletInfo :account-name="record.accountName" :account-no="text" :show-account-name="true" :show-account-no="true"/>
            </template>
            <template slot="sfOppAccountNo" slot-scope="text, record">
              <WalletInfo :account-name="record.oppAccountName" :account-no="text" :show-account-name="true" :show-account-no="true"/>
            </template>
          </a-table>
        </a-card>

        <!-- 分润记录 -->
        <a-card v-if="orderData.profitShareList && orderData.profitShareList.length > 0  && $authority('taxOrderFee')" :bordered="false" class="detail-section" size="small" title="分润记录">
          <a-table
              :columns="profitShareColumns"
              :data-source="orderData.profitShareList || []"
              :pagination="false"
              row-key="id"
              size="small"
          >
            <template slot="shareAmount" slot-scope="text">
              <Balance :amount="text" prefix="￥"></Balance>
            </template>
            <template slot="recordStatus" slot-scope="text">
              <a-tag :color="taxDict.profitShareStatus[text]?.color || 'default'">
                {{ taxDict.profitShareStatus[text]?.label || text }}
              </a-tag>
            </template>
            <template slot="shareTime" slot-scope="text">
              {{ formatDateTime(text) }}
            </template>
            <template slot="failReason" slot-scope="text">
              <span v-if="text" style="color: #ff4d4f;">{{ text }}</span>
              <span v-else>-</span>
            </template>
            <template slot="remark" slot-scope="text">
              <span>{{ text || '-' }}</span>
            </template>
            <template slot="action" slot-scope="text, record">
              <a-button
                  v-if="record.paymentStatus === 3"
                  icon="redo"
                  size="small"
                  type="link"
                  @click="retryProfitShare(record)"
              >
                重试
              </a-button>
            </template>
            <template slot="psPayerAccount" slot-scope="text, record">
              <WalletInfo :account-name="record.accountName" :account-no="text" :show-account-name="true" :show-account-no="true"/>
            </template>
            <template slot="psAccount" slot-scope="text, record">
              <WalletInfo :account-name="record.oppAccountName" :account-no="text" :show-account-name="true" :show-account-no="true"/>
            </template>
          </a-table>
        </a-card>
      </div>

      <div v-else class="empty-state">
        <a-empty description="暂无订单数据"/>
      </div>
    </a-card>
  </div>
</template>

<script>
import {message} from "ant-design-vue";
import Balance from "@/view/components/Balance.vue";
import WalletInfo from "@/view/components/WalletInfo.vue";
import {taxDict} from '@/utils/bizDict/taxDict';

export default {
  name: 'TaxOrderDetail',
  components: {WalletInfo, Balance},
  data() {
    return {
      orderNo: '',
      orderData: null,

      taxDict,

      serviceFeeColumns: [
        {title: "付款账户", dataIndex: "accountNo", align: "center", width: 180, scopedSlots: {customRender: 'sfAccountNo'}},
        {title: "收款账户", dataIndex: "oppAccountNo", align: "center", width: 180, scopedSlots: {customRender: 'sfOppAccountNo'}},
        {title: "金额", dataIndex: "feeAmount", align: "center", width: 100, scopedSlots: {customRender: 'feeAmount'}},
        {title: "支付状态", dataIndex: "paymentStatus", align: "center", width: 80, scopedSlots: {customRender: 'recordStatus'}},
        {title: "结算时间", dataIndex: "settleTime", align: "center", width: 160, scopedSlots: {customRender: 'settleTime'}},
        {title: "失败原因", dataIndex: "failReason", align: "center", width: 150, scopedSlots: {customRender: 'failReason'}},
        {title: "摘要", dataIndex: "remark", align: "center", width: 120, scopedSlots: {customRender: 'remark'}}
      ],

      profitShareColumns: [
        {title: "付款账户", dataIndex: "accountNo", align: "center", width: 180, scopedSlots: {customRender: 'psPayerAccount'}},
        {title: "渠道商账号", dataIndex: "oppAccountNo", align: "center", width: 180, scopedSlots: {customRender: 'psAccount'}},
        {title: "分润金额", dataIndex: "shareAmount", align: "center", width: 100, scopedSlots: {customRender: 'shareAmount'}},
        {title: "支付状态", dataIndex: "paymentStatus", align: "center", width: 80, scopedSlots: {customRender: 'recordStatus'}},
        {title: "分润时间", dataIndex: "shareTime", align: "center", width: 160, scopedSlots: {customRender: 'shareTime'}},
        {title: "失败原因", dataIndex: "failReason", align: "center", width: 150, scopedSlots: {customRender: 'failReason'}},
        {title: "摘要", dataIndex: "remark", align: "center", width: 120, scopedSlots: {customRender: 'remark'}},
        {title: "操作", key: "action", align: "center", width: 80, scopedSlots: {customRender: 'action'}}
      ]
    };
  },
  mounted() {
    this.orderNo = this.$route.query.orderNo;
    if (this.orderNo) {
      this.loadOrderDetail();
    }
  },
  methods: {
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      try {
        const date = new Date(dateTime);
        return date.toLocaleString('zh-CN');
      } catch (e) {
        return dateTime;
      }
    },

    async loadOrderDetail() {
      try {
        const res = await this.api.getOrderDetail(this.orderNo);
        if (res.code === 200) {
          this.orderData = res.data;
        } else {
          message.warning(res.info || "获取订单详情失败");
        }
      } catch (error) {
        console.error("获取订单详情失败:", error);
        message.error("获取订单详情失败");
      }
    },

    async retryServiceFee() {
      if (!this.orderData?.serviceFeeRecord) return;
      try {
        const res = await this.api.retryServiceFee({
          orderNo: this.orderData.orderNo
        });
        if (res.code === 200) {
          message.success("服务费重试成功");
          this.loadOrderDetail();
        } else {
          message.warning(res.info || "重试失败");
        }
      } catch (error) {
        console.error("重试失败:", error);
        message.error("重试失败");
      }
    },

    async retryProfitShare(record) {
      try {
        const res = await this.api.retryProfitShare({
          orderNo: record.orderNo,
          shareRuleId: record.shareRuleId
        });
        if (res.code === 200) {
          message.success("分润重试成功");
          this.loadOrderDetail();
        } else {
          message.warning(res.info || "重试失败");
        }
      } catch (error) {
        console.error("重试失败:", error);
        message.error("重试失败");
      }
    }
  }
};
</script>

<style lang="less" scoped>
.top-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-content {
  .detail-section {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    /deep/ .ant-card-head {
      min-height: 40px;

      .ant-card-head-title {
        font-size: 14px;
        font-weight: 600;
      }
    }
  }

  .detail-value {
    font-weight: 500;
    color: #333;
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}
</style>